import type { FC } from 'react';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { motion } from 'motion/react';

export interface ProgressSegment {
  id: string;
  label?: string;
  isCompleted: boolean;
  isCurrent: boolean;
  isClickable: boolean;
}

export type ProgressBarColor =
  | 'dark'
  | 'primary'
  | 'bank'
  | 'taxes'
  | 'expenses'
  | 'revenues';

interface SmartActivationProgressBarProps {
  segments: ProgressSegment[];
  onSegmentClick?: (segmentId: string) => void;
  color?: ProgressBarColor;
}

const StepButton = motion(Button);

// Color theme mapping (similar to Progress.component.tsx)
const colorTheme = {
  completed: {
    dark: 'bg-gray-600',
    primary: 'bg-primary-600',
    bank: 'bg-bank',
    taxes: 'bg-taxes',
    expenses: 'bg-expenses',
    revenues: 'bg-revenues',
  },
  current: {
    dark: 'bg-gray-400',
    primary: 'bg-primary-400',
    bank: 'bg-bank/70',
    taxes: 'bg-taxes-300',
    expenses: 'bg-expenses/70',
    revenues: 'bg-revenues/70',
  },
  background: {
    dark: 'bg-gray-200',
    primary: 'bg-primary-100',
    bank: 'bg-bank-100',
    taxes: 'bg-taxes-100',
    expenses: 'bg-expenses-100',
    revenues: 'bg-revenues-100',
  },
};

/**
 * Smart progress bar styled like StepsBar from UserSignup
 * Features:
 * - Clickable segments for navigation (if enabled)
 * - Automatic completion status calculation
 * - Smooth animations
 * - Configurable color schemes (defaults to 'bank')
 */
const SmartOnboardingProgressBar: FC<SmartActivationProgressBarProps> = ({
  segments,
  onSegmentClick,
  color = 'bank',
}) => (
  <div className="w-full">
    {/* Progress bar with segments (StepsBar style) */}
    <div
      className={classNames(
        'flex flex-1 rounded-xl',
        colorTheme.background[color],
      )}
    >
      {segments.map((segment, index) => {
        const isClickable = segment.isClickable && onSegmentClick;
        const isFirst = index === 0;
        const isLast = index === segments.length - 1;

        return (
          <StepButton
            key={segment.id}
            asDefault
            disabled={!isClickable}
            onClick={isClickable ? () => onSegmentClick(segment.id) : undefined}
            className={classNames('flex-1 h-2', {
              [colorTheme.background[color]]:
                !segment.isCompleted && !segment.isCurrent,
              'rounded-l-xl': isFirst,
              'rounded-r-xl':
                isLast || (segment.isClickable && !segment.isCompleted),
              [colorTheme.completed[color]]:
                segment.isCompleted || segment.isCurrent,
            })}
            aria-label={segment.label || `Step ${index + 1}`}
            title={segment.label}
          />
        );
      })}
    </div>
  </div>
);

export default SmartOnboardingProgressBar;
